// ============================================================================
// 0. ALL IMPORTS
// ============================================================================
import React, { useState, useEffect, useRef } from 'react';
import { 
  MessageCircle, Star, Send, X, Copy, CheckCircle, User, Loader2, ShoppingBag, Search, Users, 
  ChevronRight, ChevronLeft, ArrowLeft, Grid, Play, Video, Image as ImageIcon, Plus, Bookmark, 
  Trash2, Flame, Globe, Heart, Tag, Sparkles, Clock, MapPin, DollarSign, MessageSquare, Share2
} from 'lucide-react';
import { 
  collection, query, orderBy, onSnapshot, addDoc, serverTimestamp, doc, updateDoc, 
  arrayUnion, arrayRemove, where, getDocs, setDoc, increment, deleteDoc, getDoc 
} from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useAuth } from '../../hooks/useAuth';

// ============================================================================
// 1. HELPERS & SUB-COMPONENTS
// ============================================================================
const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Recently';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return date.toLocaleDateString();
};

const fetchUserList = async (userIds: string[]) => {
    if (!userIds || userIds.length === 0) return [];
    try {
        const idsToFetch = userIds.slice(0, 15); 
        const usersData = await Promise.all(idsToFetch.map(async (uid) => {
            const snap = await getDoc(doc(db, 'users', uid));
            return snap.exists() ? { uid: snap.id, ...snap.data() } : null;
        }));
        return usersData.filter(u => u !== null);
    } catch (error) { return []; }
};

const AuthorInfo = ({ authorId, fallbackName, fallbackImage, onClick, onDelete, isOwner }: any) => {
    const [author, setAuthor] = useState({ name: fallbackName || 'User', image: fallbackImage });

    useEffect(() => {
        if (!authorId) return;
        const fetchAuthor = async () => {
            try {
                const userDoc = await getDoc(doc(db, 'users', authorId));
                if (userDoc.exists()) {
                    const data = userDoc.data();
                    setAuthor({ name: data.displayName || data.username || fallbackName || 'User', image: data.photoURL || data.photoUrl || data.avatar || fallbackImage });
                }
            } catch (e) { console.error(e); }
        };
        fetchAuthor();
    }, [authorId, fallbackName, fallbackImage]);

    return (
        <div className="flex items-center justify-between w-full">
            <button className="flex items-center gap-3 text-left group flex-1" onClick={onClick}>
                <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-full lg:rounded-none bg-zinc-900 flex items-center justify-center text-white font-bold text-sm overflow-hidden transition-transform group-active:scale-95 shrink-0 border border-zinc-800">
                    {author.image ? <img src={author.image} referrerPolicy="no-referrer" className="w-full h-full object-cover grayscale" alt="Author"/> : <User size={18}/>}
                </div>
                <div className="min-w-0">
                    <p className="text-sm font-black text-white uppercase tracking-widest leading-none truncate group-hover:underline underline-offset-4">{author.name}</p>
                    <p className="text-[10px] text-zinc-500 mt-1 font-mono uppercase tracking-widest">Network</p>
                </div>
            </button>
            {isOwner && (
                <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="p-2 text-zinc-600 hover:text-red-500 transition-colors rounded-full lg:rounded-none">
                    <Trash2 size={16} />
                </button>
            )}
        </div>
    );
};

const ImageCarousel = ({ images, title, onDoubleTap }: { images: string[], title: string, onDoubleTap?: () => void }) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [showStar, setShowStar] = useState(false);
    const imageList = (images && images.length > 0) ? images : ["https://placehold.co/800x800/111/444?text=NO+SIGNAL"];

    const handleScroll = () => {
        if (scrollRef.current) {
            const index = Math.round(scrollRef.current.scrollLeft / scrollRef.current.clientWidth);
            setCurrentIndex(index);
        }
    };

    const scrollTo = (index: number) => {
        if (scrollRef.current) scrollRef.current.scrollTo({ left: index * scrollRef.current.clientWidth, behavior: 'smooth' });
    };

    const handleDoubleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (onDoubleTap) {
            setShowStar(true);
            onDoubleTap();
            setTimeout(() => setShowStar(false), 1000);
        }
    };

    return (
        <div className="relative w-full h-full group bg-zinc-950 overflow-hidden cursor-pointer" onDoubleClick={handleDoubleClick}>
            <div ref={scrollRef} onScroll={handleScroll} className="flex w-full h-full overflow-x-auto snap-x snap-mandatory scrollbar-hide">
                {imageList.map((img, i) => (
                    <img key={i} src={img} className="w-full h-full object-cover shrink-0 snap-center select-none grayscale group-hover:grayscale-0 transition-all duration-700" alt={`${title} ${i + 1}`} />
                ))}
            </div>

            {showStar && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-30 animate-in zoom-in duration-300 fade-out delay-500">
                    <Star size={120} className="text-white fill-white drop-shadow-[0_0_40px_rgba(0,0,0,0.5)]" />
                </div>
            )}

            {imageList.length > 1 && (
                <>
                    <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-1.5 z-20 pointer-events-none">
                        {imageList.map((_, idx) => (<div key={idx} className={`h-1.5 transition-all ${idx === currentIndex ? 'bg-white w-6 rounded-full lg:rounded-none' : 'bg-white/40 w-1.5 rounded-full lg:rounded-none'}`} />))}
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); scrollTo(currentIndex > 0 ? currentIndex - 1 : imageList.length - 1); }} className="hidden lg:flex absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 text-white items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity border border-white/20 backdrop-blur-md hover:bg-white hover:text-black rounded-none"><ChevronLeft size={20}/></button>
                    <button onClick={(e) => { e.stopPropagation(); scrollTo(currentIndex < imageList.length - 1 ? currentIndex + 1 : 0); }} className="hidden lg:flex absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 text-white items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity border border-white/20 backdrop-blur-md hover:bg-white hover:text-black rounded-none"><ChevronRight size={20}/></button>
                </>
            )}
        </div>
    );
};

// ============================================================================
// 2. COMMENTS MODAL
// ============================================================================
const CommentsModal = ({ post, isOpen, onClose, userProfile, requireAuth }: any) => {
  const { user } = useAuth();
  const [comments, setComments] = useState<any[]>([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!post?.id || !isOpen) return;
    const q = query(collection(db, "posts", post.id, "comments"), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
        setComments(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsubscribe();
  }, [post?.id, isOpen]);

  const handleSend = () => {
    requireAuth(async () => {
      if (!newComment.trim() || !user || !post?.id) return;
      setLoading(true);
      try {
          await addDoc(collection(db, "posts", post.id, "comments"), {
              text: newComment.trim(),
              userId: user.uid, userName: userProfile?.displayName || user.displayName || 'User',
              userAvatar: userProfile?.photoURL || userProfile?.photoUrl || userProfile?.avatar || user.photoURL || '', createdAt: serverTimestamp()
          });
          await updateDoc(doc(db, "posts", post.id), { comments: increment(1) });
          setNewComment(''); 
      } catch (e) { console.error(e); } finally { setLoading(false); }
    }, "Log in to join the conversation.");
  };

  const handleDeleteComment = async (commentId: string) => {
      if (!window.confirm("Delete this transmission?")) return;
      try {
          await deleteDoc(doc(db, "posts", post.id, "comments", commentId));
          await updateDoc(doc(db, "posts", post.id), { comments: increment(-1) });
      } catch (e) { console.error("Error deleting comment", e); }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[999] bg-black/80 backdrop-blur-sm flex flex-col justify-end lg:justify-center lg:items-center animate-in fade-in duration-200">
        <div className="bg-zinc-950 h-[85vh] lg:h-[80vh] lg:w-full lg:max-w-xl flex flex-col rounded-t-[2rem] lg:rounded-none shadow-2xl border-t lg:border border-zinc-800 animate-in slide-in-from-bottom-full duration-300">
            <div className="p-6 border-b border-zinc-900 flex justify-between items-center bg-black rounded-t-[2rem] lg:rounded-none shrink-0 z-10">
                <div className="flex flex-col">
                  <h3 className="text-white font-black uppercase tracking-widest text-sm">Discussion</h3>
                  <p className="text-zinc-500 font-mono text-[10px] uppercase">Post by @{post?.authorName || post?.userName || 'User'}</p>
                </div>
                <button onClick={onClose} className="p-2 bg-zinc-900 text-zinc-400 hover:text-white rounded-full lg:rounded-none transition-colors"><X size={18}/></button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4 space-y-6 flex flex-col-reverse scrollbar-hide bg-zinc-950">
                {comments.length === 0 ? (
                  <div className="text-center text-zinc-600 text-xs py-10 uppercase font-bold tracking-widest">It's quiet here. Say something.</div>
                ) : comments.map(c => {
                    const isMe = c.userId === user?.uid;
                    return (
                      <div key={c.id} className={`flex w-full ${isMe ? 'justify-end' : 'justify-start'} animate-in slide-in-from-bottom-2 group`}>
                          <div className={`flex max-w-[85%] gap-2 ${isMe ? 'flex-row-reverse' : 'flex-row'}`}>
                              {!isMe && (
                                  <div className="w-8 h-8 rounded-full bg-zinc-800 shrink-0 overflow-hidden mt-auto border border-zinc-700">
                                      {c.userAvatar ? <img src={c.userAvatar} className="w-full h-full object-cover grayscale" /> : <User size={14} className="m-auto mt-2 text-zinc-500" />}
                                  </div>
                              )}
                              <div className={`flex flex-col ${isMe ? 'items-end' : 'items-start'}`}>
                                  {!isMe && <span className="text-[9px] text-zinc-500 font-black uppercase tracking-widest ml-1 mb-1">{c.userName}</span>}
                                  <div className={`relative p-4 text-sm font-medium shadow-md ${isMe ? 'bg-white text-black rounded-2xl rounded-br-sm' : 'bg-zinc-800 text-white rounded-2xl rounded-bl-sm'}`}>
                                      <p className="font-mono">{c.text}</p>
                                      {isMe && (
                                          <button onClick={() => handleDeleteComment(c.id)} className="absolute -top-2 -left-2 p-1.5 bg-black border border-zinc-800 text-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                                              <Trash2 size={10} />
                                          </button>
                                      )}
                                  </div>
                              </div>
                          </div>
                      </div>
                    );
                })}
            </div>
            
            <div className="p-4 border-t border-zinc-900 bg-black flex flex-col gap-3 pb-safe shrink-0">
                <div className="flex items-center gap-3">
                    <input value={newComment} onChange={e => setNewComment(e.target.value)} placeholder="Drop a thought..." className="flex-1 bg-zinc-900 rounded-full px-5 py-4 text-white font-mono text-sm outline-none focus:border-white border border-transparent transition-all placeholder:text-zinc-600" onKeyDown={e => e.key === 'Enter' && handleSend()}/>
                    <button onClick={handleSend} disabled={!newComment.trim() || loading} className="w-14 h-14 rounded-full bg-white text-black flex items-center justify-center disabled:opacity-50 hover:bg-zinc-200 transition-colors shrink-0 shadow-lg">
                        {loading ? <Loader2 size={18} className="animate-spin"/> : <Send size={18}/>}
                    </button>
                </div>
            </div>
        </div>
    </div>
  );
};

// ============================================================================
// 3. SHARE MODAL (unchanged, but we keep it here)
// ============================================================================
const ShareModal = ({ isOpen, onClose, shareData, userProfile, requireAuth }: any) => {
    const { user } = useAuth();
    const [view, setView] = useState<'menu' | 'users'>('menu');
    const [search, setSearch] = useState('');
    const [results, setResults] = useState<any[]>([]);
    const [suggested, setSuggested] = useState<any[]>([]);
    const [sendingTo, setSendingTo] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (userProfile?.followingUsers && userProfile.followingUsers.length > 0) {
            fetchUserList(userProfile.followingUsers).then(setSuggested);
        }
    }, [userProfile]);

    useEffect(() => {
        if (view === 'users') {
            if (search.length > 0) {
                const q = query(
                    collection(db, 'users'), 
                    where('displayName', '>=', search),
                    where('displayName', '<=', search + '\uf8ff')
                );
                getDocs(q).then(snap => {
                    let res = snap.docs.map(d => ({ uid: d.id, ...d.data() as any }));
                    res = res.filter(u => {
                        const isBrand = u.isBrand === true || 
                                        String(u.isBrand).toLowerCase() === 'true' || 
                                        String(u.role).toLowerCase() === 'brand' || 
                                        String(u.accountType).toLowerCase() === 'brand' || 
                                        String(u.accountType).toLowerCase() === 'business' ||
                                        u.businessName; 
                        return !isBrand;
                    });
                    setResults(res);
                });
            } else { setResults([]); }
        }
    }, [view, search]);

    const handleCopy = () => {
        let link = window.location.origin; 
        if (shareData?.type === 'post') {
            const post = shareData.payload;
            // Normal post link (thrifts are handled in standalone ThriftView, not here)
            link += `/post/${post.id}`;
        } else {
            link += window.location.pathname;
        }
        navigator.clipboard.writeText(link);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleSendToUser = (targetUser: any) => {
      requireAuth(async () => {
        if (!user || !shareData) return;
        setSendingTo(targetUser.uid);
        try {
            const chatId = [user.uid, targetUser.uid].sort().join('_');
            const chatRef = doc(db, 'chats', chatId);
            let sharePayload: any = {
                text: "",
                senderId: user.uid, 
                createdAt: serverTimestamp(), 
                read: false
            };
            let previewText = "Shared an asset";
            if (shareData.type === 'post') {
                const post = shareData.payload;
                sharePayload.messageType = 'post_share';
                sharePayload.post = { 
                    type: 'post', id: post.id, 
                    authorName: post.authorName || post.userName,
                    authorId: post.authorId || post.userId,
                    authorImage: post.authorImage || post.userPhoto,
                    title: post.title || post.caption || 'Fit Check',
                    image: post.image || (post.images && post.images[0]) || post.imageUrl,
                    videoUrl: post.videoUrl || post.video || null
                };
            }
            await setDoc(chatRef, {
                participants: [user.uid, targetUser.uid],
                participantDetails: { 
                    [user.uid]: { displayName: userProfile?.displayName || 'User', photoURL: userProfile?.photoURL || userProfile?.photoUrl || userProfile?.avatar || '' },
                    [targetUser.uid]: { displayName: targetUser.displayName, photoURL: targetUser.photoURL || targetUser.photoUrl || targetUser.avatar || '' }
                },
                lastMessage: { text: previewText, createdAt: serverTimestamp(), unread: true, senderId: user.uid },
                updatedAt: serverTimestamp()
            }, { merge: true });
            await addDoc(collection(db, 'chats', chatId, 'messages'), sharePayload);
            setSendingTo(null); onClose(); 
        } catch (e) { console.error("Failed to send", e); setSendingTo(null); }
      }, "Log in to share this with a friend.");
    };

    if (!isOpen || !shareData) return null;
    const displayList = search.length > 0 ? results : suggested;
    const listTitle = search.length > 0 ? "Search Results" : "Suggested Contacts";

    const renderPreview = () => {
        if (shareData.type === 'post') {
            const post = shareData.payload;
            return (
                <div className="flex gap-4 items-center">
                    <div className="w-16 h-16 bg-zinc-900 rounded-xl lg:rounded-none overflow-hidden shrink-0 border border-zinc-800 flex items-center justify-center relative">
                        {post.videoUrl ? (
                            <>
                                <video src={post.videoUrl} className="w-full h-full object-cover grayscale" muted playsInline />
                                <Play size={16} className="absolute text-white/70" />
                            </>
                        ) : (post.image || (post.images && post.images[0]) || post.imageUrl) ? (
                            <img src={post.image || (post.images && post.images[0]) || post.imageUrl} className="w-full h-full object-cover grayscale" alt="Preview"/>
                        ) : (
                            <ImageIcon size={24} className="text-zinc-600" />
                        )}
                    </div>
                    <div className="min-w-0 flex-1">
                        <p className="font-bold text-white text-sm truncate uppercase">{post.title || post.caption || "Asset"}</p>
                        <p className="text-zinc-500 text-[10px] font-bold uppercase truncate mt-1">By @{post.authorName || post.userName}</p>
                    </div>
                </div>
            );
        }
    };

    return (
        <div className="fixed inset-0 z-[999] bg-black/90 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in">
            <div className="w-full max-w-sm lg:max-w-md bg-zinc-950 border border-zinc-800 rounded-[2rem] lg:rounded-none flex flex-col max-h-[80vh] shadow-2xl overflow-hidden">
                <div className="p-5 border-b border-zinc-900 flex justify-between items-center">
                    {view === 'users' ? <button onClick={() => setView('menu')} className="p-2 bg-black rounded-full lg:rounded-none text-white hover:bg-zinc-800 transition-colors"><ArrowLeft size={16}/></button> : <h3 className="text-sm font-black text-white uppercase tracking-widest ml-2">Share</h3>}
                    <button onClick={onClose} className="p-2 bg-black rounded-full lg:rounded-none text-zinc-500 hover:text-white transition-colors"><X size={18}/></button>
                </div>
                <div className="p-5 flex-1 overflow-y-auto scrollbar-hide">
                    {view === 'menu' && (
                        <div className="space-y-4">
                            <div className="p-4 bg-black rounded-2xl lg:rounded-none border border-zinc-900 mb-6">
                                {renderPreview()}
                            </div>
                            <button onClick={handleCopy} className="w-full p-4 rounded-2xl lg:rounded-none text-black font-bold text-sm flex items-center justify-center gap-3 transition-all active:scale-95 bg-white hover:bg-zinc-200 uppercase tracking-widest">
                                {copied ? <CheckCircle size={18} className="text-black"/> : <Copy size={18}/>} {copied ? "Link Copied" : "Copy Link"}
                            </button>
                            <button onClick={() => requireAuth(() => setView('users'), "Log in to share internally via DM.")} className="w-full p-4 rounded-2xl lg:rounded-none bg-black text-white border border-zinc-800 hover:border-white font-bold text-sm flex items-center justify-between transition-all active:scale-95 uppercase tracking-widest">
                                <div className="flex items-center gap-3"><Send size={18}/> Send in DM</div>
                                <ChevronRight size={16} className="text-zinc-500"/>
                            </button>
                        </div>
                    )}
                    {view === 'users' && (
                        <div className="space-y-6">
                            <div className="relative">
                                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" size={16}/>
                                <input autoFocus className="w-full bg-black rounded-full lg:rounded-none border border-zinc-800 py-3 pl-12 pr-4 text-sm text-white outline-none focus:border-white placeholder:text-zinc-600 transition-all font-mono" placeholder="Search users..." value={search} onChange={(e) => setSearch(e.target.value)}/>
                            </div>
                            <h4 className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest px-2">{listTitle}</h4>
                            <div className="space-y-2">
                                {displayList.map(u => (
                                    <button key={u.uid} disabled={sendingTo === u.uid} onClick={() => handleSendToUser(u)} className="w-full flex items-center justify-between p-3 bg-black rounded-2xl lg:rounded-none border border-zinc-900 hover:border-white transition-all disabled:opacity-50 group">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full lg:rounded-none bg-zinc-900 border border-zinc-800 overflow-hidden flex items-center justify-center text-white">
                                                {u.photoURL || u.photoUrl || u.avatar ? <img src={u.photoURL || u.photoUrl || u.avatar} referrerPolicy="no-referrer" className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all" alt=""/> : <User size={16}/>}
                                            </div>
                                            <div className="text-left">
                                                <p className="text-white font-black uppercase tracking-widest text-xs">{u.displayName}</p>
                                                <p className="text-zinc-500 font-mono text-[9px] mt-0.5 uppercase">@{u.username || 'user'}</p>
                                            </div>
                                        </div>
                                        {sendingTo === u.uid ? <Loader2 size={16} className="animate-spin text-white"/> : <div className="bg-white text-black text-[10px] font-bold px-4 py-1.5 rounded-full lg:rounded-none opacity-0 group-hover:opacity-100 transition-opacity uppercase">Send</div>}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// ============================================================================
// 4. UNIFIED POST MODAL (Image, Video, Text – Thrift layout removed because we won't use it here)
// ============================================================================
const DesktopPostModal = ({ post, isOpen, onClose, onSelectProduct, onLike, onShare, onViewProfile, onSave, onDelete, userProfile, onMessage, requireAuth }: any) => {
    const { user } = useAuth();
    const [comments, setComments] = useState<any[]>([]);
    const [newComment, setNewComment] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!post?.id || !isOpen) return;
        const q = query(collection(db, "posts", post.id, "comments"), orderBy("createdAt", "desc"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setComments(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        return () => unsubscribe();
    }, [post?.id, isOpen]);

    const handleSendComment = () => {
      requireAuth(async () => {
        if (!newComment.trim() || !user || !post?.id) return;
        setLoading(true);
        try {
            await addDoc(collection(db, "posts", post.id, "comments"), {
                text: newComment, userId: user.uid, userName: userProfile?.displayName || user.displayName || 'User',
                userAvatar: userProfile?.photoURL || userProfile?.photoUrl || userProfile?.avatar || user.photoURL || '', createdAt: serverTimestamp()
            });
            await updateDoc(doc(db, "posts", post.id), { comments: increment(1) });
            setNewComment('');
        } catch (e) { console.error(e); } finally { setLoading(false); }
      }, "Log in to post a reply.");
    };

    if (!isOpen || !post) return null;

    let items = post.outfitItems || post.items || post.taggedItems || [];
    if (items.length === 0 && post.taggedProductName) {
        items = [{
            id: post.taggedProductId || Math.random().toString(), name: post.taggedProductName,
            image: post.taggedProductImage || post.image, price: post.taggedProductPrice || 0, brand: 'Tagged Item'
        }];
    }
    const validItems = items.filter((i: any) => i && (i.name || i.image));
    const isOwner = (post.authorId || post.userId) === user?.uid;
    const displayMedia = post.videoUrl || post.video || post.image || (post.images && post.images[0]) || post.imageUrl || post.img;

    // ========================================================================
    // TWITTER-STYLE SINGLE COLUMN MODAL (For Text Posts)
    // ========================================================================
    if (post.type === 'thought') {
        return (
            <div className="fixed inset-0 z-[800] bg-black/90 backdrop-blur-md flex justify-center items-center animate-in fade-in p-4 sm:p-6 overflow-y-auto">
                <div className="w-full max-w-xl bg-black border border-zinc-800 rounded-2xl lg:rounded-none flex flex-col max-h-full shadow-2xl relative my-auto" onClick={(e) => e.stopPropagation()}>
                    <button onClick={onClose} className="absolute top-4 right-4 p-2 text-zinc-500 hover:text-white transition-colors z-10 bg-black rounded-full lg:rounded-none border border-zinc-800 hover:border-zinc-600">
                        <X size={20} />
                    </button>
                    
                    <div className="flex-1 overflow-y-auto scrollbar-hide">
                        <div className="p-6 sm:p-8 border-b border-zinc-900">
                            <div className="flex items-center justify-between mb-6 pr-10">
                                <AuthorInfo 
                                    authorId={post.authorId || post.userId || post.uid} 
                                    fallbackName={post.authorName || post.userName} 
                                    fallbackImage={post.authorImage || post.userAvatar} 
                                    isOwner={isOwner}
                                    onDelete={() => { onDelete(post.id); onClose(); }}
                                />
                            </div>
                            
                            <p className="text-xl sm:text-2xl font-medium text-white mb-6 leading-relaxed break-words whitespace-pre-wrap font-sans">{post.caption}</p>
                            
                            <div className="flex gap-6 py-4 border-y border-zinc-800 text-zinc-500 text-sm font-bold">
                                <span><strong className="text-white">{post.likes?.length || 0}</strong> Likes</span>
                                <span><strong className="text-white">{post.comments || 0}</strong> Replies</span>
                            </div>

                            <div className="flex justify-between py-2 border-b border-zinc-900">
                                <button onClick={() => onLike(post, post.likes || [])} className="p-3 text-zinc-500 hover:text-white transition-colors flex items-center gap-2 group">
                                    <Heart size={20} className={post.likes?.includes(user?.uid) ? "fill-white text-red-500" : "group-hover:text-white"} />
                                </button>
                                <button className="p-3 text-zinc-500 hover:text-white transition-colors cursor-default">
                                    <MessageCircle size={20} />
                                </button>
                                <button onClick={() => onSave(post.id, 'post')} className="p-3 text-zinc-500 hover:text-white transition-colors">
                                    <Bookmark size={20} fill={userProfile?.savedPosts?.includes(post.id) ? "white" : "none"} className={userProfile?.savedPosts?.includes(post.id) ? "text-white" : ""} />
                                </button>
                                <button onClick={() => onShare(post)} className="p-3 text-zinc-500 hover:text-white transition-colors">
                                    <Share2 size={20} />
                                </button>
                            </div>

                            <div className="mt-6 space-y-6">
                                {comments.length > 0 ? comments.map((c, i) => (
                                    <div key={i} className="flex gap-4 group">
                                        <div className="w-10 h-10 rounded-full lg:rounded-none bg-zinc-900 overflow-hidden shrink-0 border border-zinc-800 flex items-center justify-center">
                                            {c.userAvatar ? <img src={c.userAvatar} referrerPolicy="no-referrer" className="w-full h-full object-cover"/> : <User size={18} className="text-zinc-500"/>}
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center justify-between">
                                                <p className="text-white font-bold text-sm">{c.userName || 'User'}</p>
                                            </div>
                                            <p className="text-zinc-300 text-sm mt-1 leading-relaxed font-sans">{c.text}</p>
                                        </div>
                                    </div>
                                )) : <p className="text-zinc-600 text-sm font-mono uppercase tracking-widest text-center py-6 border border-dashed border-zinc-900 rounded-xl lg:rounded-none">Be the first to reply</p>}
                            </div>
                        </div>
                    </div>
                    
                    <div className="p-4 border-t border-zinc-800 bg-zinc-950 shrink-0 rounded-b-2xl lg:rounded-none">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full lg:rounded-none bg-zinc-800 overflow-hidden shrink-0 border border-zinc-700 flex items-center justify-center">
                                {userProfile?.photoURL || userProfile?.avatar ? <img src={userProfile.photoURL || userProfile.avatar} referrerPolicy="no-referrer" className="w-full h-full object-cover grayscale" /> : <User size={16} className="text-zinc-500"/>}
                            </div>
                            <input value={newComment} onChange={(e) => setNewComment(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSendComment()} placeholder="Post your reply..." className="flex-1 bg-transparent text-sm text-white outline-none placeholder:text-zinc-600 font-sans" />
                            <button onClick={handleSendComment} disabled={!newComment.trim() || loading} className="px-5 py-2 bg-white text-black font-black uppercase tracking-widest text-[10px] rounded-full lg:rounded-none disabled:opacity-50 hover:bg-zinc-200 transition-colors">
                                {loading ? <Loader2 size={14} className="animate-spin"/> : 'Reply'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // ========================================================================
    // STANDARD MODAL (For Images/Videos)
    // ========================================================================
    return (
        <div className="fixed inset-0 z-[800] bg-black flex flex-col lg:flex-row animate-in fade-in pb-safe">
            <button onClick={onClose} className="absolute top-4 left-4 lg:top-8 lg:left-8 z-50 p-3 lg:p-4 bg-black/60 lg:bg-zinc-900 border border-white/20 lg:border-zinc-800 hover:bg-white hover:text-black text-white transition-colors shadow-2xl rounded-full lg:rounded-none backdrop-blur-md">
                <ArrowLeft size={20} className="lg:w-6 lg:h-6" />
            </button>
            
            <div className="w-full h-full flex flex-col lg:flex-row overflow-hidden">
                <div className="w-full lg:w-[65%] h-[45vh] lg:h-full shrink-0 bg-zinc-950 border-b lg:border-b-0 lg:border-r border-zinc-900 relative flex items-center justify-center">
                    <div className="w-full h-full flex items-center justify-center">
                        <ImageCarousel images={post.images || [displayMedia]} title={post.title || post.caption} onDoubleTap={() => !post.likes?.includes(user?.uid) && onLike(post, post.likes || [])} />
                    </div>
                </div>
                
                <div className="w-full lg:w-[35%] flex flex-col flex-1 bg-black overflow-hidden">
                    <div className="p-6 lg:p-8 border-b border-zinc-900 flex items-center justify-between bg-zinc-950 shrink-0">
                        <AuthorInfo 
                            authorId={post.authorId || post.userId || post.uid} 
                            fallbackName={post.authorName || post.userName} 
                            fallbackImage={post.authorImage || post.userAvatar} 
                            onClick={() => requireAuth(() => { onClose(); onViewProfile(post.authorId || post.userId || post.uid); }, "Log in to view this profile.")}
                            isOwner={isOwner}
                            onDelete={() => { onDelete(post.id); onClose(); }}
                        />
                        <button onClick={() => onShare(post)} className="text-zinc-500 hover:text-white transition-colors p-3 border border-zinc-800 bg-black rounded-none ml-4"><Send size={20}/></button>
                    </div>

                    <div className="flex-1 overflow-y-auto p-6 lg:p-10 space-y-8 lg:space-y-12 scrollbar-hide">
                        <div>
                            <p className="text-zinc-400 font-mono text-sm leading-relaxed">{post.description || post.caption}</p>
                        </div>

                        {validItems.length > 0 && (
                            <div className="border-y border-zinc-900 py-8 lg:py-10">
                                <h3 className="text-[10px] font-black text-zinc-500 uppercase tracking-[0.2em] mb-6 flex items-center gap-2"><ShoppingBag size={14}/> Gear Used</h3>
                                <div className="grid grid-cols-2 gap-4">
                                    {validItems.map((item: any, idx: number) => (
                                        <button key={idx} onClick={() => requireAuth(() => { onClose(); onSelectProduct(item); }, "Log in to shop this gear.")} className="text-left group border border-zinc-900 bg-zinc-950 p-4 hover:border-white transition-colors rounded-none">
                                            <div className="w-full aspect-square bg-black overflow-hidden mb-4 relative border border-zinc-900 rounded-none">
                                                <img src={item.image} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"/>
                                                <div className="absolute inset-0 bg-black/80 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-[10px] font-black tracking-widest uppercase">Inspect</div>
                                            </div>
                                            <p className="text-white text-xs font-black uppercase truncate tracking-widest mb-1">{item.name}</p>
                                            <p className="text-zinc-500 font-mono text-[9px] uppercase tracking-wider truncate">R {item.price}</p>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div>
                            <h3 className="text-[10px] font-black text-zinc-500 uppercase tracking-[0.2em] mb-6 flex items-center gap-2"><MessageCircle size={14}/> Network Comms</h3>
                            <div className="space-y-6">
                                {comments.length === 0 ? <p className="text-zinc-600 font-mono text-[10px] uppercase tracking-widest border border-dashed border-zinc-900 p-6 text-center rounded-none">No Comments yet.</p> : comments.map(c => (
                                    <div key={c.id} className="flex gap-4">
                                        <div className="w-10 h-10 bg-black border border-zinc-800 overflow-hidden shrink-0 flex items-center justify-center text-white text-xs font-bold rounded-none">
                                            {c.userAvatar ? <img src={c.userAvatar} referrerPolicy="no-referrer" className="w-full h-full object-cover grayscale"/> : <User size={16}/>}
                                        </div>
                                        <div>
                                            <p className="text-white text-sm font-mono"><span className="font-black uppercase tracking-widest text-xs mr-3">{c.userName}</span><span className="text-zinc-400">{c.text}</span></p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="border-t border-zinc-900 p-6 lg:p-8 bg-zinc-950 shrink-0">
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex gap-4">
                                <button onClick={() => onLike(post, post.likes || [])} className={`p-3 lg:p-4 border transition-colors rounded-none ${post.likes?.includes(user?.uid) ? 'bg-white text-black border-white' : 'bg-black text-white border-zinc-800 hover:border-zinc-500'}`}>
                                    <Star size={20} className="lg:w-6 lg:h-6" fill={post.likes?.includes(user?.uid) ? "currentColor" : "none"} />
                                </button>
                                <button onClick={() => onSave(post.id, 'post')} className="text-zinc-500 hover:text-white transition-colors p-3 lg:p-4 border border-zinc-800 bg-black rounded-none">
                                    <Bookmark size={20} fill={userProfile?.savedPosts?.includes(post.id) ? "white" : "none"} className={`lg:w-6 lg:h-6 ${userProfile?.savedPosts?.includes(post.id) ? "text-white" : ""}`} />
                                </button>
                            </div>
                            <p className="text-white font-mono text-xs uppercase tracking-widest">{post.likes?.length || 0} Stars</p>
                        </div>
                        
                        <div className="flex items-center gap-4 bg-black border border-zinc-800 p-1.5 lg:p-2 focus-within:border-white transition-colors rounded-none">
                            <input value={newComment} onChange={e => setNewComment(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSendComment()} placeholder="Drop a thought..." className="flex-1 bg-transparent text-white text-xs font-mono outline-none placeholder:text-zinc-700 px-4" />
                            <button onClick={handleSendComment} disabled={!newComment.trim() || loading} className="text-black bg-white font-black text-[10px] uppercase tracking-widest disabled:opacity-30 hover:bg-zinc-200 px-4 lg:px-6 py-3 lg:py-4 transition-colors rounded-none">Broadcast</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ============================================================================
// 5. HOOK PLAYER (unchanged)
// ============================================================================
const HookPlayer = ({ post, onLike, user, onSelectProduct, onShare, onComment, onViewProfile, isSaved, onSave, onDelete, requireAuth }: any) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showStar, setShowStar] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          videoRef.current?.play().catch(() => console.log("Autoplay blocked"));
          setIsPlaying(true);
        } else {
          videoRef.current?.pause();
          setIsPlaying(false);
        }
      },
      { threshold: 0.6 } 
    );
    if (videoRef.current) observer.observe(videoRef.current);
    return () => { if (videoRef.current) observer.unobserve(videoRef.current); };
  }, []);

  const handleDoubleClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      requireAuth(() => {
          setShowStar(true);
          if (!post.likes?.includes(user?.uid)) {
              onLike(post, post.likes || []);
          }
          setTimeout(() => setShowStar(false), 1000);
      }, "Log in to like this video.");
  };

  const handleSingleClick = (e: React.MouseEvent) => {
      if (e.detail === 1) {
          if (videoRef.current?.paused) {
              videoRef.current.play(); setIsPlaying(true);
          } else {
              videoRef.current?.pause(); setIsPlaying(false);
          }
      }
  };

  const featuredItem = post.outfitItems?.[0];
  const isOwner = (post.authorId || post.userId) === user?.uid;
  const isLiked = post.likes?.includes(user?.uid);

  return (
    <div className="relative w-full h-full bg-black snap-start snap-always flex items-center justify-center overflow-hidden">
      {showStar && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-30 animate-in zoom-in duration-300 fade-out delay-500">
              <Star size={120} className="text-white fill-white drop-shadow-[0_0_40px_rgba(0,0,0,0.5)]" />
          </div>
      )}

      <video
        ref={videoRef} src={post.videoUrl} loop muted playsInline 
        onClick={handleSingleClick}
        onDoubleClick={handleDoubleClick}
        className="w-full h-full object-cover cursor-pointer grayscale"
      />
      <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black via-black/40 to-transparent pointer-events-none" />

      <div className="absolute right-4 bottom-24 flex flex-col items-center gap-6 z-10">
        <button onClick={() => requireAuth(() => onViewProfile(post.authorId || post.userId || post.uid), "Log in to view this profile.")} className="w-12 h-12 rounded-full lg:rounded-none overflow-hidden border-2 border-white shadow-lg mb-2 bg-zinc-900 flex items-center justify-center text-white font-bold transition-transform active:scale-95">
           {post.userPhoto || post.authorImage ? <img src={post.userPhoto || post.authorImage} referrerPolicy="no-referrer" className="w-full h-full object-cover" /> : <User size={20}/>}
        </button>
        
        <button onClick={() => requireAuth(() => onLike(post, post.likes || []), "Log in to like this video.")} className="flex flex-col items-center gap-1 group">
            <div className={`w-12 h-12 rounded-full lg:rounded-none flex items-center justify-center transition-all ${isLiked ? 'bg-white text-black' : 'bg-black/50 backdrop-blur-md text-white border border-white/20 group-hover:bg-white group-hover:text-black'}`}>
                <Star size={24} fill={isLiked ? "currentColor" : "none"} />
            </div>
            <span className="text-white font-bold text-[10px]">{post.likes?.length || 0}</span>
        </button>

        <button onClick={() => requireAuth(() => onComment(), "Log in to join the conversation.")} className="flex flex-col items-center gap-1 group">
            <div className="w-12 h-12 bg-black/50 backdrop-blur-md rounded-full lg:rounded-none flex items-center justify-center border border-white/20 text-white group-hover:bg-white group-hover:text-black transition-colors">
                <MessageCircle size={24} />
            </div>
            <span className="text-white font-bold text-[10px]">{post.comments || 0}</span>
        </button>
        
        <button onClick={() => requireAuth(() => onSave(post.id, 'post'), "Log in to save this to your archive.")} className="flex flex-col items-center gap-1 group">
            <div className={`w-12 h-12 rounded-full lg:rounded-none flex items-center justify-center transition-all ${isSaved ? 'bg-white text-black' : 'bg-black/50 backdrop-blur-md text-white border border-white/20 group-hover:bg-white group-hover:text-black'}`}>
                <Bookmark size={24} fill={isSaved ? "currentColor" : "none"} />
            </div>
            <span className="text-white font-bold text-[10px]">Save</span>
        </button>

        <button onClick={() => requireAuth(() => onShare(), "Log in to share this with your network.")} className="flex flex-col items-center gap-1 group">
            <div className="w-12 h-12 bg-black/50 backdrop-blur-md rounded-full lg:rounded-none flex items-center justify-center border border-white/20 text-white group-hover:bg-white group-hover:text-black transition-colors">
                <Send size={22} />
            </div>
            <span className="text-white font-bold text-[10px]">Share</span>
        </button>

        {isOwner && (
            <button onClick={() => onDelete(post.id)} className="flex flex-col items-center gap-1 group mt-2">
                <div className="w-10 h-10 bg-black/50 backdrop-blur-md rounded-full lg:rounded-none flex items-center justify-center border border-red-500/50 text-red-400 group-hover:bg-red-500 group-hover:text-white transition-colors">
                    <Trash2 size={18} />
                </div>
            </button>
        )}
      </div>

      <div className="absolute left-4 bottom-8 right-20 z-10 flex flex-col gap-4">
        <div>
            <button onClick={() => requireAuth(() => onViewProfile(post.authorId || post.userId || post.uid), "Log in to view this profile.")} className="text-white font-black text-sm uppercase tracking-widest mb-1 drop-shadow-md hover:underline underline-offset-4 text-left">@{post.userName || post.authorName}</button>
            <p className="text-zinc-200 text-sm leading-relaxed line-clamp-2 drop-shadow-md font-mono uppercase">{post.caption || post.description}</p>
        </div>
        {featuredItem && (
            <button onClick={() => requireAuth(() => onSelectProduct(featuredItem), "Log in to shop this gear.")} className="flex items-center gap-3 p-2 pr-4 bg-black/50 backdrop-blur-md border border-white/20 rounded-2xl lg:rounded-none w-max hover:bg-white hover:text-black group transition-all">
                <div className="w-10 h-10 bg-white/10 rounded-xl lg:rounded-none overflow-hidden shrink-0">
                    <img src={featuredItem.image} className="w-full h-full object-cover grayscale group-hover:grayscale-0" />
                </div>
                <div className="text-left">
                    <p className="text-[9px] font-bold text-zinc-300 group-hover:text-zinc-600 uppercase tracking-widest">Tagged Gear</p>
                    <p className="text-xs font-black text-white group-hover:text-black uppercase truncate max-w-[120px]">{featuredItem.name}</p>
                </div>
            </button>
        )}
      </div>

      {!isPlaying && (
         <div className="absolute inset-0 flex items-center justify-center pointer-events-none bg-black/10">
             <div className="w-20 h-20 bg-black/50 backdrop-blur-md rounded-full lg:rounded-none flex items-center justify-center text-white border border-white/20">
                 <div className="w-0 h-0 border-t-[10px] border-t-transparent border-l-[16px] border-l-white border-b-[10px] border-b-transparent ml-2"></div>
             </div>
         </div>
      )}
    </div>
  );
};

// ============================================================================
// 6. MAIN COMPONENT: COMMUNITY VIEW (Stills & Hooks only)
// ============================================================================
interface CommunityViewProps {
    onViewProfile: (userId: string) => void;
    onSelectProduct: (product: any) => void;
    onOpenCreatePost?: () => void;
    onOpenCreateHook?: () => void; 
    onMessageUser?: (userId: string, initialMessage?: string) => void; 
    requireAuth?: (action: () => void, message?: string) => void;
}

export const CommunityView: React.FC<CommunityViewProps> = ({ 
    onViewProfile, onSelectProduct, onOpenCreatePost, onOpenCreateHook, onMessageUser, requireAuth = (action) => action()
}) => {
  const { user } = useAuth();
  const [userProfile, setUserProfile] = useState<any>(null);

  useEffect(() => {
      if (!user?.uid) return;
      const unsub = onSnapshot(doc(db, "users", user.uid), (docSnap) => {
          if (docSnap.exists()) setUserProfile(docSnap.data());
      });
      return () => unsub();
  }, [user]);
  
  // VIEW MODES – only 'stills' and 'hooks' now
  const [viewMode, setViewMode] = useState<'stills' | 'hooks'>('stills');
  const [activeTab, setActiveTab] = useState<'foryou' | 'following'>('foryou');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Data States
  const [stills, setStills] = useState<any[]>([]);
  const [hooks, setHooks] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  const [allNetworkUsers, setAllNetworkUsers] = useState<any[]>([]);
  const [searchedUsers, setSearchedUsers] = useState<any[]>([]);
  const [discoverUsers, setDiscoverUsers] = useState<any[]>([]);
  const [followedUsers, setFollowedUsers] = useState<any[]>([]);
  
  const [activeCommentPost, setActiveCommentPost] = useState<any | null>(null);
  const [shareData, setShareData] = useState<{type: 'post'|'message', payload: any} | null>(null);
  const [activeDesktopPost, setActiveDesktopPost] = useState<any | null>(null);

  // EXPLORE STATES
  const [exploreCategory, setExploreCategory] = useState<string | null>(null);
  const [exploreSearch, setExploreSearch] = useState('');

  // Firestore listener – exclude thrift posts entirely
  useEffect(() => {
    setIsLoading(true);
    const q = query(collection(db, "posts"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, (snapshot) => {
        const allPosts = snapshot.docs.map(doc => {
            const data = doc.data() as any;
            let outfitItems = data.outfitItems || data.items || data.taggedItems || [];
            if (outfitItems.length === 0 && data.taggedProductName) {
                outfitItems = [{ id: data.taggedProductId, name: data.taggedProductName, image: data.taggedProductImage, price: data.taggedProductPrice, brand: 'Tagged' }];
            }
            return { id: doc.id, ...data, likes: data.likes || [], outfitItems: outfitItems, comments: data.comments || 0 };
        });

        // Exclude thrift posts completely
        const nonThriftPosts = allPosts.filter((post: any) => post.type !== 'thrift');
        setHooks(nonThriftPosts.filter((post: any) => post.contentType === 'hook'));
        setStills(nonThriftPosts.filter((post: any) => post.contentType !== 'hook')); // includes text posts ('thought')
        
        setIsLoading(false);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
      const fetchNetwork = async () => {
          try {
              const q = query(collection(db, 'users'));
              const snap = await getDocs(q);
              let users = snap.docs.map(doc => ({ uid: doc.id, ...doc.data() as any }));
              users = users.filter(u => {
                  const isBrand = u.isBrand === true || 
                                  String(u.isBrand).toLowerCase() === 'true' || 
                                  String(u.role).toLowerCase() === 'brand' || 
                                  String(u.accountType).toLowerCase() === 'brand' || 
                                  String(u.accountType).toLowerCase() === 'business' ||
                                  u.businessName; 
                  return !isBrand;
              });
              setAllNetworkUsers(users);
              if (user) {
                  const discoverPool = users.filter(u => u.uid !== user.uid);
                  setDiscoverUsers(discoverPool);
              }
          } catch (error) { console.error("Network Error:", error); }
      };
      fetchNetwork();
  }, [user]);

  // URL sharing for posts (only non-thrift now)
  useEffect(() => {
      const params = new URLSearchParams(window.location.search);
      const sharedPostId = params.get('post');
      if (sharedPostId && !isLoading) {
          const hookPost = hooks.find(p => p.id === sharedPostId);
          const stillPost = stills.find(p => p.id === sharedPostId);
          const foundPost = hookPost || stillPost;
          if (foundPost) {
              if (hookPost) setViewMode('hooks');
              else setViewMode('stills');
              setActiveDesktopPost(foundPost);
              window.history.replaceState({}, '', window.location.pathname);
          }
      }
  }, [stills, hooks, isLoading]);

  useEffect(() => {
      if (searchTerm.length > 0) {
          const term = searchTerm.toLowerCase();
          const matched = allNetworkUsers.filter(u => 
              (u.displayName || '').toLowerCase().includes(term) || 
              (u.username || '').toLowerCase().includes(term) ||
              (u.handle || '').toLowerCase().includes(term)
          );
          setSearchedUsers(matched);
      } else { 
          setSearchedUsers([]); 
      }
  }, [searchTerm, allNetworkUsers]);

  useEffect(() => {
      if (userProfile?.followingUsers && userProfile.followingUsers.length > 0) {
          fetchUserList(userProfile.followingUsers).then(setFollowedUsers);
      } else {
          setFollowedUsers([]);
      }
  }, [userProfile?.followingUsers]);

  const handleLike = (post: any, currentLikes: string[]) => {
    requireAuth(async () => {
      if (!user) return;
      const postRef = doc(db, "posts", post.id);
      try {
          if (currentLikes.includes(user.uid)) {
              await updateDoc(postRef, { likes: arrayRemove(user.uid) });
          } else {
              await updateDoc(postRef, { likes: arrayUnion(user.uid) });
          }
      } catch (e) { console.error(e); }
    }, "Log in to like this post.");
  };

  const handleSaveItem = (itemId: string, type: 'post') => {
    requireAuth(async () => {
      if (!user) return;
      const userRef = doc(db, "users", user.uid);
      const currentArray = userProfile?.savedPosts || [];
      try {
          if (currentArray.includes(itemId)) {
              await updateDoc(userRef, { savedPosts: arrayRemove(itemId) });
          } else {
              await updateDoc(userRef, { savedPosts: arrayUnion(itemId) });
          }
      } catch(e) { console.error(e); }
    }, "Log in to save this to your archive.");
  };

  const handleDeletePost = async (postId: string) => {
      if (!window.confirm("Erase asset from the network?")) return;
      try { await deleteDoc(doc(db, "posts", postId)); } catch(e) { console.error(e); }
  };

  const filteredStills = stills.filter(post => {
      if (activeTab === 'following' && !userProfile?.followingUsers?.includes(post.authorId || post.userId)) return false;
      if (searchTerm && !(post.caption || post.description || '').toLowerCase().includes(searchTerm.toLowerCase())) return false;
      return true;
  });

  const filteredHooks = hooks.filter(post => {
      if (activeTab === 'following' && !userProfile?.followingUsers?.includes(post.authorId || post.userId)) return false;
      if (searchTerm && !(post.caption || post.description || '').toLowerCase().includes(searchTerm.toLowerCase())) return false;
      return true;
  });

  const famePosts = [...stills].filter(p => (p.likes?.length || 0) > 0 && p.type !== 'thought').sort((a,b) => (b.likes?.length || 0) - (a.likes?.length || 0)).slice(0, 10);
  
  let displayUsers = searchTerm.length > 0 ? searchedUsers : (activeTab === 'following' ? followedUsers : discoverUsers);
  let listTitle = searchTerm.length > 0 ? "Search Results" : (activeTab === 'following' ? "Following" : "Discover Users");

  const handleMoreLikeThis = (category?: string) => {
      if (category) {
          setExploreCategory(category);
      } else {
          alert("This post doesn't have a category tag yet.");
      }
  };

  return (
    <div className="relative w-full h-[100dvh] bg-black text-white font-sans overflow-hidden flex flex-col">
        
        {/* GLOBAL MODALS */}
        <CommentsModal post={activeCommentPost} isOpen={!!activeCommentPost} onClose={() => setActiveCommentPost(null)} userProfile={userProfile} requireAuth={requireAuth} />
        <ShareModal shareData={shareData} isOpen={!!shareData} onClose={() => setShareData(null)} userProfile={userProfile} requireAuth={requireAuth} />
        
        <DesktopPostModal 
            post={activeDesktopPost} 
            isOpen={!!activeDesktopPost} 
            onClose={() => setActiveDesktopPost(null)} 
            onSelectProduct={(p: any) => { setActiveDesktopPost(null); onSelectProduct(p); }}
            onLike={(post: any) => handleLike(post, post.likes || [])} 
            onShare={(post: any) => setShareData({ type: 'post', payload: post })}
            onViewProfile={onViewProfile}
            onSave={handleSaveItem}
            onDelete={handleDeletePost}
            userProfile={userProfile}
            onMessage={onMessageUser}
            requireAuth={requireAuth}
        />

        {/* HEADER – only two buttons now */}
        <div className="absolute top-0 inset-x-0 z-40 bg-gradient-to-b from-black/95 via-black/80 to-transparent pt-safe pb-8 pointer-events-none">
            <div className="px-4 lg:px-8 flex flex-col lg:flex-row items-center justify-between pointer-events-auto mt-2 max-w-7xl mx-auto gap-4">
                <h1 className="text-2xl font-black italic tracking-tighter uppercase drop-shadow-lg text-center lg:text-left w-full lg:w-auto">Community</h1>
                
                <div className="flex w-full lg:w-auto bg-black/80 backdrop-blur-md border border-zinc-800 p-1 rounded-full lg:rounded-none shadow-2xl">
                    <button onClick={() => setViewMode('stills')} className={`flex-1 lg:flex-none justify-center px-4 lg:px-6 py-2.5 rounded-full lg:rounded-none flex items-center gap-2 transition-all ${viewMode === 'stills' ? 'bg-white text-black' : 'text-zinc-400 hover:text-white'}`}>
                        <Grid size={14} /> <span className={`text-[10px] uppercase tracking-widest ${viewMode === 'stills' ? 'font-black' : 'font-bold'}`}>Stills</span>
                    </button>
                    <button onClick={() => setViewMode('hooks')} className={`flex-1 lg:flex-none justify-center px-4 lg:px-6 py-2.5 rounded-full lg:rounded-none flex items-center gap-2 transition-all border-l border-zinc-800 ${viewMode === 'hooks' ? 'bg-white text-black' : 'text-zinc-400 hover:text-white'}`}>
                        <Play size={14} /> <span className={`text-[10px] uppercase tracking-widest ${viewMode === 'hooks' ? 'font-black' : 'font-bold'}`}>Hooks</span>
                    </button>
                </div>
            </div>
        </div>

        {/* STILLS MODE */}
        {viewMode === 'stills' && (
            <div className="flex-1 overflow-y-auto pt-32 lg:pt-24 pb-32 px-4 lg:px-8 w-full scrollbar-hide">
                
                <div className="max-w-6xl mx-auto">
                    <div className="mb-8 space-y-5">
                        <div className="flex gap-2">
                            <button onClick={() => setActiveTab('foryou')} className={`px-6 py-2 rounded-full lg:rounded-none text-[10px] font-bold uppercase tracking-widest transition-colors ${activeTab === 'foryou' ? 'bg-white text-black' : 'bg-zinc-900 text-white hover:bg-zinc-800'}`}>For You</button>
                            <button onClick={() => setActiveTab('following')} className={`px-6 py-2 rounded-full lg:rounded-none text-[10px] font-bold uppercase tracking-widest transition-colors ${activeTab === 'following' ? 'bg-white text-black' : 'bg-zinc-900 text-white hover:bg-zinc-800'}`}>Following</button>
                        </div>
                        <div className="relative">
                            <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-zinc-500" size={16} />
                            <input className="w-full bg-zinc-900/50 border border-zinc-800 rounded-full lg:rounded-none py-3 pl-12 pr-4 text-sm text-white placeholder:text-zinc-600 outline-none focus:border-white transition-all font-mono" placeholder="Search network..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                        </div>
                    </div>

                    {displayUsers.length > 0 && (
                        <div className="mb-8 border-b border-zinc-900 pb-6">
                            <h3 className="text-[10px] font-bold text-zinc-500 mb-4 uppercase tracking-widest flex items-center gap-2 pl-2"><Users size={14}/> {listTitle}</h3>
                            <div className="flex overflow-x-auto gap-4 pb-2 scrollbar-hide">
                                {displayUsers.map((u: any) => (
                                    <div key={u.uid} className="flex flex-col items-center gap-2 min-w-[72px] cursor-pointer group" onClick={() => requireAuth(() => onViewProfile(u.uid), "Log in to view this profile.")}>
                                        <div className="w-16 h-16 rounded-full lg:rounded-none bg-zinc-900 border border-zinc-800 group-hover:border-white flex items-center justify-center text-white overflow-hidden transition-all">
                                            {u.photoURL || u.photoUrl || u.avatar ? <img src={u.photoURL || u.photoUrl || u.avatar} referrerPolicy="no-referrer" className="w-full h-full object-cover grayscale group-hover:grayscale-0" /> : <User size={20}/>}
                                        </div>
                                        <p className="text-white font-bold text-[9px] uppercase tracking-widest truncate w-full text-center">{u.displayName}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {famePosts.length > 0 && !searchTerm && (
                        <div className="mb-10 border-b border-zinc-900 pb-8">
                            <div className="mb-4 flex items-center gap-3">
                                <h2 className="text-sm font-black uppercase italic tracking-widest text-white border-l-4 border-white pl-3 flex items-center gap-2"><Flame size={16}/> Scoop of Fame</h2>
                            </div>
                            <div className="flex overflow-x-auto gap-6 snap-x scrollbar-hide pb-4">
                                {famePosts.map(post => (
                                    <div key={post.id} className="relative w-56 aspect-[4/5] bg-zinc-900 shrink-0 snap-start border border-zinc-800 group cursor-pointer rounded-2xl lg:rounded-none overflow-hidden" onClick={() => requireAuth(() => onViewProfile(post.authorId || post.userId || post.uid), "Log in to view this profile.")}>
                                        <img src={post.image || post.images?.[0]} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700" />
                                        <div className="absolute top-4 left-4 bg-white text-black font-black text-[9px] uppercase tracking-widest px-3 py-1 shadow-xl rounded-full lg:rounded-none">
                                            FAME // {post.likes?.length || 0} ⭐
                                        </div>
                                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4 pt-10">
                                            <p className="text-xs text-white font-black uppercase truncate">@{post.authorName || post.userName}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className="space-y-12 w-full max-w-xl mx-auto mt-10">
                    {filteredStills.length === 0 ? (
                        <div className="text-center py-20 bg-zinc-950 rounded-[2rem] lg:rounded-none border border-dashed border-zinc-800">
                            <ImageIcon size={48} className="mx-auto text-zinc-700 mb-4" />
                            <p className="text-white font-bold uppercase tracking-widest text-sm mb-1">No Posts Yet</p>
                        </div>
                    ) : (
                        filteredStills.map(post => (
                            <div key={post.id} className="relative group w-full shadow-2xl rounded-[2rem] lg:rounded-none bg-black border border-zinc-800 overflow-hidden">
                                <div className="flex lg:block overflow-x-auto lg:overflow-visible snap-x snap-mandatory scrollbar-hide w-full h-full">
                                    {post.type === 'thought' ? (
                                        <div className="w-full min-w-full shrink-0 snap-center flex flex-col relative z-20 bg-black rounded-[2rem] lg:rounded-none p-5 lg:p-6 cursor-pointer hover:bg-zinc-950/50 transition-colors" onClick={() => requireAuth(() => setActiveDesktopPost(post), "Log in to view this post.")}>
                                            <AuthorInfo 
                                                authorId={post.authorId || post.userId || post.uid} 
                                                fallbackName={post.authorName || post.userName} 
                                                fallbackImage={post.authorImage || post.userAvatar} 
                                                onClick={(e:any) => { e.stopPropagation(); requireAuth(() => onViewProfile(post.authorId || post.userId || post.uid), "Log in to view this profile."); }} 
                                                isOwner={(post.authorId || post.userId || post.uid) === user?.uid}
                                                onDelete={() => handleDeletePost(post.id)}
                                            />
                                            <div className="mt-4 mb-6 pointer-events-none">
                                                <p className="text-white text-base sm:text-lg leading-relaxed whitespace-pre-wrap break-words font-sans">{post.caption || post.description}</p>
                                            </div>
                                            <div className="flex items-center justify-between pt-4 border-t border-zinc-900" onClick={e => e.stopPropagation()}>
                                                <div className="flex gap-6">
                                                    <button onClick={() => handleLike(post, post.likes || [])} className="flex items-center gap-2 group/like text-zinc-500 hover:text-white transition-colors">
                                                        <Star size={18} className={post.likes?.includes(user?.uid) ? 'fill-white text-white' : ''} />
                                                        <span className="font-bold text-xs group-hover/like:text-white transition-colors">{post.likes?.length || 0}</span>
                                                    </button>
                                                    <button onClick={() => requireAuth(() => setActiveCommentPost(post), "Log in to join the conversation.")} className="flex items-center gap-2 text-zinc-500 hover:text-white transition-colors">
                                                        <MessageCircle size={18}/>
                                                        <span className="font-bold text-xs">{post.comments || 0}</span>
                                                    </button>
                                                    <button onClick={() => requireAuth(() => setShareData({ type: 'post', payload: post }), "Log in to share this with your network.")} className="flex items-center gap-2 text-zinc-500 hover:text-white transition-colors">
                                                        <Send size={18} />
                                                    </button>
                                                </div>
                                                <button onClick={() => handleSaveItem(post.id, 'post')} className="text-zinc-500 hover:text-white transition-colors">
                                                    <Bookmark size={18} fill={userProfile?.savedPosts?.includes(post.id) ? "white" : "none"} className={userProfile?.savedPosts?.includes(post.id) ? "text-white" : ""} />
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="w-full min-w-full shrink-0 snap-center flex flex-col relative z-20 bg-black rounded-[2rem] lg:rounded-none">
                                            <div className="p-4 border-b border-zinc-900 bg-zinc-950 shrink-0 rounded-t-[2rem] lg:rounded-none">
                                                <AuthorInfo 
                                                    authorId={post.authorId || post.userId || post.uid} 
                                                    fallbackName={post.authorName || post.userName} 
                                                    fallbackImage={post.authorImage || post.userAvatar} 
                                                    onClick={() => requireAuth(() => onViewProfile(post.authorId || post.userId || post.uid), "Log in to view this profile.")} 
                                                    isOwner={(post.authorId || post.userId || post.uid) === user?.uid}
                                                    onDelete={() => handleDeletePost(post.id)}
                                                />
                                            </div>
                                            <div className="relative w-full border-y border-zinc-900 bg-zinc-950 overflow-hidden">
                                                <ImageCarousel images={post.images || [post.image]} title={post.title || "Post"} onDoubleTap={() => !post.likes.includes(user?.uid) && handleLike(post, post.likes)} />
                                                {(post.outfitItems?.length > 0 || post.taggedProductName) && (
                                                    <button 
                                                        onClick={() => requireAuth(() => onSelectProduct(post.outfitItems?.[0] || { id: post.taggedProductId, name: post.taggedProductName, image: post.taggedProductImage, price: post.taggedProductPrice, brand: 'Tagged' }), "Log in to shop this gear.")}
                                                        className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-md p-1.5 pr-4 rounded-full flex items-center gap-3 shadow-[0_10px_30px_rgba(0,0,0,0.5)] max-w-[80%] z-20 hover:scale-105 active:scale-95 transition-all group"
                                                    >
                                                        <div className="w-8 h-8 bg-black rounded-full overflow-hidden flex-shrink-0 border border-zinc-200">
                                                            <img src={post.outfitItems?.[0]?.image || post.taggedProductImage} className="w-full h-full object-cover"/>
                                                        </div>
                                                        <div className="overflow-hidden text-left">
                                                            <p className="text-[9px] text-zinc-500 font-bold uppercase tracking-widest leading-none">Shop</p>
                                                            <p className="text-xs font-black text-black truncate uppercase leading-tight mt-0.5">{post.outfitItems?.[0]?.name || post.taggedProductName}</p>
                                                        </div>
                                                    </button>
                                                )}
                                            </div>
                                            <div className="p-6 bg-black shrink-0 rounded-b-[2rem] lg:rounded-none">
                                                <div className="flex items-center justify-between mb-6">
                                                    <button onClick={() => handleLike(post, post.likes || [])} className="flex items-center gap-2 group/like">
                                                        <Star size={26} className={`transition-colors ${post.likes?.includes(user?.uid) ? 'fill-white text-white' : 'text-zinc-500 group-hover/like:text-white'}`} />
                                                        <span className="font-bold text-sm text-zinc-300 group-hover/like:text-white transition-colors">{post.likes?.length || 0}</span>
                                                    </button>
                                                    <div className="flex gap-4">
                                                        <button onClick={() => requireAuth(() => setActiveCommentPost(post), "Log in to join the conversation.")} className="flex items-center gap-2 text-zinc-500 hover:text-white transition-colors">
                                                            <MessageCircle size={24}/>
                                                            <span className="font-bold text-sm">{post.comments || 0}</span>
                                                        </button>
                                                        <button onClick={() => handleSaveItem(post.id, 'post')} className="text-zinc-500 hover:text-white transition-colors">
                                                            <Bookmark size={24} fill={userProfile?.savedPosts?.includes(post.id) ? "white" : "none"} className={`lg:w-6 lg:h-6 ${userProfile?.savedPosts?.includes(post.id) ? "text-white" : ""}`} />
                                                        </button>
                                                        <button onClick={() => requireAuth(() => setShareData({ type: 'post', payload: post }), "Log in to share this with your network.")} className="text-zinc-500 hover:text-white transition-colors ml-2">
                                                            <Send size={24} />
                                                        </button>
                                                    </div>
                                                </div>
                                                <p className="text-sm font-mono text-zinc-300 uppercase leading-relaxed mt-4">
                                                    <span className="font-black text-white mr-3">{post.authorName || post.userName}</span>{post.caption || post.description}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                    <div className="lg:hidden w-24 shrink-0 snap-center bg-zinc-950 flex flex-col items-center justify-center border-l border-zinc-800 z-10">
                                        <button onClick={() => handleMoreLikeThis(post.category)} className="flex flex-col items-center gap-3 text-zinc-500 hover:text-white transition-colors group/explore">
                                            <div className="w-12 h-12 rounded-full border border-zinc-700 bg-black flex items-center justify-center shadow-[0_0_20px_rgba(255,255,255,0.1)] group-hover/explore:scale-110 group-hover/explore:border-white transition-all">
                                                <Sparkles size={20} className="group-hover/explore:text-white" />
                                            </div>
                                            <span className="text-[9px] font-black uppercase text-center tracking-widest leading-tight">Explore<br/>Vibe</span>
                                        </button>
                                    </div>
                                </div>
                                {post.type !== 'thought' && (
                                    <button 
                                        onClick={() => handleMoreLikeThis(post.category)}
                                        className="hidden lg:flex absolute -right-16 top-1/2 -translate-y-1/2 flex-col items-center gap-3 text-zinc-500 hover:text-white transition-all opacity-0 group-hover:opacity-100 group-hover:translate-x-4 z-0"
                                    >
                                        <div className="w-14 h-14 rounded-full border border-zinc-700 bg-zinc-950 flex items-center justify-center shadow-[0_0_20px_rgba(255,255,255,0.1)] hover:scale-110 hover:border-white transition-all">
                                            <Sparkles size={24} className="text-white" />
                                        </div>
                                        <span className="text-[9px] font-black uppercase tracking-widest bg-black px-2 py-1 rounded-md border border-zinc-800">Explore Vibe</span>
                                    </button>
                                )}
                            </div>
                        ))
                    )}
                </div>
            </div>
        )}

        {/* HOOKS MODE */}
        {viewMode === 'hooks' && (
            <div className="h-[calc(100dvh)] w-full overflow-y-scroll snap-y snap-mandatory scrollbar-hide bg-black relative">
                {filteredHooks.length === 0 ? (
                     <div className="h-full flex flex-col items-center justify-center text-zinc-600">
                         <Video size={48} strokeWidth={1} className="mb-4 opacity-50" />
                         <span className="font-bold text-xs uppercase tracking-widest">No Hooks Found</span>
                     </div>
                ) : (
                    filteredHooks.map((hook) => (
                        <div key={hook.id} className="snap-start snap-always h-full w-full">
                            <HookPlayer 
                                post={hook} 
                                user={user} 
                                onLike={handleLike} 
                                onSelectProduct={onSelectProduct} 
                                onShare={() => setShareData({ type: 'post', payload: hook })} 
                                onComment={() => setActiveCommentPost(hook)} 
                                onViewProfile={onViewProfile}
                                isSaved={userProfile?.savedPosts?.includes(hook.id)}
                                onSave={handleSaveItem}
                                onDelete={handleDeletePost}
                                requireAuth={requireAuth}
                            />
                        </div>
                    ))
                )}
            </div>
        )}

        {/* EXPLORE OVERLAY (unchanged) */}
        {exploreCategory && (
            <div className="fixed inset-0 z-[100] bg-black animate-in slide-in-from-bottom duration-300 flex flex-col pb-safe">
                <div className="shrink-0 bg-black pt-safe z-20 border-b border-zinc-900">
                    <div className="flex items-center gap-3 p-3 max-w-5xl mx-auto">
                        <button onClick={() => { setExploreCategory(null); setExploreSearch(''); }} className="p-2 text-white hover:bg-zinc-900 rounded-full transition-colors shrink-0">
                            <ArrowLeft size={24} />
                        </button>
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
                            <input className="w-full bg-zinc-900 rounded-xl py-2 pl-10 pr-4 text-sm text-white outline-none focus:bg-zinc-800 transition-colors placeholder:text-zinc-500 font-mono" placeholder={`Search in ${exploreCategory}...`} value={exploreSearch} onChange={(e) => setExploreSearch(e.target.value)} />
                        </div>
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto scrollbar-hide bg-black">
                    <div className="grid grid-cols-3 gap-[2px] sm:gap-1 max-w-5xl mx-auto">
                        {stills.filter(p => p.category === exploreCategory && p.type !== 'thought' && (!exploreSearch || (p.caption || p.description || '').toLowerCase().includes(exploreSearch.toLowerCase()))).map((post) => (
                            <button key={post.id} onClick={() => requireAuth(() => setActiveDesktopPost(post), "Log in to view this post.")} className="relative group bg-zinc-900 overflow-hidden cursor-pointer aspect-square w-full">
                                <img src={post.image || post.images?.[0]} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-300" alt="Explore" />
                                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
                                    <div className="flex items-center gap-4 text-white font-black text-xs lg:text-sm">
                                        <span className="flex items-center gap-1.5"><Star size={16} className="fill-white" /> {post.likes?.length || 0}</span>
                                        <span className="flex items-center gap-1.5"><MessageCircle size={16} className="fill-white" /> {post.comments || 0}</span>
                                    </div>
                                </div>
                                {post.images?.length > 1 && <div className="absolute top-2 right-2 text-white drop-shadow-md"><ImageIcon size={16} /></div>}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        )}
    </div>
  );
};